import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "./firebase";
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES } from "./serviceCatalog";
import { optimizeImageForUpload } from "./imageUploadUtils";

const AI_CHAT_URL =
  process.env.REACT_APP_AI_CHAT_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/aiChat";

const CITY_ALIASES = {
  bangalore: "Bengaluru",
  banglore: "Bengaluru",
  bengaluru: "Bengaluru",
  bombay: "Mumbai",
  calcutta: "Kolkata",
  trivandrum: "Thiruvananthapuram",
  baroda: "Vadodara",
};

const CATEGORY_ALIASES = {
  vet: "Veterinary & Health Care",
  veterinary: "Veterinary & Health Care",
  doctor: "Veterinary & Health Care",
  vomit: "Veterinary & Health Care",
  vomiting: "Veterinary & Health Care",
  fever: "Veterinary & Health Care",
  diarrhea: "Veterinary & Health Care",
  diarrheaa: "Veterinary & Health Care",
  loose: "Veterinary & Health Care",
  emergency: "Veterinary & Health Care",
  injury: "Veterinary & Health Care",
  pain: "Veterinary & Health Care",
  grooming: "Grooming, Wellness & Spa",
  spa: "Grooming, Wellness & Spa",
  boarding: "Boarding, Daycare & Sitting",
  hotel: "Boarding, Daycare & Sitting",
  daycare: "Boarding, Daycare & Sitting",
  walking: "Walking, Training & Behaviour",
  training: "Walking, Training & Behaviour",
  behaviour: "Walking, Training & Behaviour",
  behavior: "Walking, Training & Behaviour",
  food: "Food, Nutrition & Meals",
  nutrition: "Food, Nutrition & Meals",
  meals: "Food, Nutrition & Meals",
  transport: "Transport, Travel & Relocation",
  travel: "Transport, Travel & Relocation",
  relocation: "Transport, Travel & Relocation",
  insurance: "Insurance & Protection",
  protection: "Insurance & Protection",
  adoption: "Adoption, Rescue & Community",
  rescue: "Adoption, Rescue & Community",
  store: "Stores, Pharmacy & Accessories",
  pharmacy: "Stores, Pharmacy & Accessories",
  accessories: "Stores, Pharmacy & Accessories",
  breeding: "Breeding & Registration",
  registration: "Breeding & Registration",
  photography: "Photography, Art & Events",
  events: "Photography, Art & Events",
  art: "Photography, Art & Events",
  memorial: "Memorial",
  cleaning: "Cleaning & Home Support",
};

const STARTER_MESSAGE =
  "Hi! I'm Petaar AI - your pet's AI Yaar 🐾 Ask me anything about pet care, health, or finding services near you.";
const VET_CITY_CHIPS = ["Bengaluru", "Mumbai", "Delhi", "Hyderabad", "Pune"];
const MAX_RAW_IMAGE_ATTACHMENT_BYTES = 12 * 1024 * 1024;
const MAX_IMAGE_ATTACHMENT_BYTES = 1200 * 1024;
const AI_MEMORY_VERSION = 3;

function normalizeMessageRole(role) {
  const normalized = String(role || "").trim().toLowerCase();
  if (["ai", "assistant", "bot", "model"].includes(normalized)) {
    return "ai";
  }
  return "user";
}

function normalizeStoredMessage(msg) {
  if (!msg || typeof msg !== "object") {
    return null;
  }
  const role = normalizeMessageRole(msg.role);
  const text = String(msg.text || msg.content || "").trim();
  if (!text && !msg.image) {
    return null;
  }
  return {
    role,
    text,
    image: msg.image && typeof msg.image === "object" ? msg.image : null,
    quickReplies: Array.isArray(msg.quickReplies) ? msg.quickReplies.slice(0, 8) : [],
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read selected image."));
    reader.readAsDataURL(file);
  });
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isFindVetNearMeQuery(value) {
  const normalized = normalizeText(value);
  if (!normalized) return false;
  if (normalized === "find vet near me") return true;
  return /(find|show|need|book|locate|nearby|nearest).*(vet).*near me/.test(normalized) ||
    /vet near me/.test(normalized);
}

function buildServiceSignature(service) {
  return [
    normalizeText(service?.title),
    normalizeText(service?.category),
    normalizeText(service?.city),
    Number(service?.price || 0),
  ].join("|");
}

function dedupeServices(serviceList) {
  const seen = new Set();
  const unique = [];
  for (const service of Array.isArray(serviceList) ? serviceList : []) {
    const signature = buildServiceSignature(service);
    if (!signature || seen.has(signature)) {
      continue;
    }
    seen.add(signature);
    unique.push(service);
  }
  return unique;
}

function normalizeCityTyposInQuery(text) {
  let normalized = String(text || "");
  for (const [alias, city] of Object.entries(CITY_ALIASES)) {
    const re = new RegExp(`\\b${alias}\\b`, "gi");
    normalized = normalized.replace(re, city);
  }
  return normalized.trim();
}

const NON_SCORING_WORDS = new Set([
  "what", "where", "when", "which", "should", "would", "could", "please",
  "need", "want", "book", "booking", "service", "services", "provider",
  "pet", "pets", "dog", "dogs", "cat", "cats", "the", "for", "with", "near",
  "city", "issue", "help", "tell",
]);

function detectCityMention(text) {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  for (const [alias, city] of Object.entries(CITY_ALIASES)) {
    if (normalized.includes(alias)) return city;
  }
  for (const cityName of MAJOR_INDIAN_CITIES) {
    const cityKey = normalizeText(cityName);
    if (cityKey && normalized.includes(cityKey)) return cityName;
  }
  return "";
}

function detectCategoryMention(text) {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  for (const category of SERVICE_CATEGORIES) {
    const fullName = normalizeText(category.name);
    if (fullName && normalized.includes(fullName)) {
      return category.name;
    }
  }
  for (const [alias, categoryName] of Object.entries(CATEGORY_ALIASES)) {
    if (normalized.includes(alias)) return categoryName;
  }
  return "";
}

function isBookingIntentQuery(text) {
  const normalized = normalizeText(text);
  if (!normalized) return false;
  return /(book|booking|consult|appointment|near me|nearby|find|need|looking for|service|provider|schedule)/.test(normalized);
}

function getAiRuntimeLabel(mode) {
  const normalized = String(mode || "").toLowerCase();
  if (normalized === "openai") return "Live AI";
  if (normalized) return "Local AI";
  return "AI";
}

function getAiRuntimeHint(mode, reason) {
  const normalizedMode = String(mode || "").toLowerCase();
  const normalizedReason = String(reason || "").toLowerCase();
  if (normalizedMode === "openai") {
    return "Using cloud model";
  }
  if (normalizedReason.includes("quota")) {
    return "Cloud quota reached, using local knowledge";
  }
  if (normalizedReason.includes("key_missing")) {
    return "Cloud key missing, using local knowledge";
  }
  if (normalizedReason.includes("paid_ai_disabled")) {
    return "Cloud AI disabled, using local knowledge";
  }
  if (normalizedMode) {
    return "Using local knowledge";
  }
  return "Runtime unknown";
}

export default function PetAIAssistant({
  services,
  onOpenProvider,
  onApplyBookingIntent,
  user,
  selectedPet,
  onHome,
}) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [petContext, setPetContext] = useState({
    breed: "",
    age: "",
    weight: "",
    symptoms: "",
  });
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: STARTER_MESSAGE,
    },
  ]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [bookingAutomation, setBookingAutomation] = useState(null);
  const [automationMessage, setAutomationMessage] = useState("");
  const [lastUserQuery, setLastUserQuery] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const [preparingImage, setPreparingImage] = useState(false);
  const [aiRuntime, setAiRuntime] = useState({
    mode: "",
    reason: "",
  });
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  const isMobileViewport = viewportWidth < 760;
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const handleLogoHomeClick = () => {
    if (typeof onHome === "function") {
      onHome();
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };
  const quickPromptChips = [
    "Find vet near me",
    "Complete homemade food recipe for dog",
    "My cat is not eating, what should I do?",
  ];
  const memoryDocId = user?.uid ? `${user.uid}_${selectedPet?.id || "default"}` : "";
  const initialPetContext = useMemo(() => ({
    breed: selectedPet?.breed || "",
    age: selectedPet?.age ? `${selectedPet.age} months` : "",
    weight: selectedPet?.weight ? `${selectedPet.weight} kg` : "",
    symptoms: "",
  }), [selectedPet?.breed, selectedPet?.age, selectedPet?.weight]);

  const persistMemory = async (nextMessages, nextContext) => {
    if (!memoryDocId) {
      return;
    }
    const safeMessages = nextMessages
      .slice(-40)
      .map((msg) => ({
        role: msg?.role || "",
        text: String(msg?.text || ""),
        quickReplies: Array.isArray(msg?.quickReplies) ? msg.quickReplies.slice(0, 8) : [],
        image: msg?.image ? {
          name: String(msg.image.name || "image"),
          type: String(msg.image.type || ""),
          size: Number(msg.image.size || 0),
        } : null,
      }));
    try {
      await setDoc(doc(db, "aiMemory", memoryDocId), {
        memoryVersion: AI_MEMORY_VERSION,
        userId: user?.uid || "",
        petId: selectedPet?.id || null,
        petContext: nextContext,
        messages: safeMessages,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      console.error("AI memory save failed:", err);
    }
  };

  useEffect(() => {
    const loadMemory = async () => {
      if (!memoryDocId) {
        return;
      }
      try {
        const snap = await getDoc(doc(db, "aiMemory", memoryDocId));
        if (!snap.exists()) {
          return;
        }
        const data = snap.data();
        const docVersion = Number(data.memoryVersion || 0);
        if (docVersion !== AI_MEMORY_VERSION) {
          setMessages([
            {
              role: "ai",
              text: STARTER_MESSAGE,
            },
          ]);
        } else if (Array.isArray(data.messages) && data.messages.length) {
          const normalizedMessages = data.messages
            .map(normalizeStoredMessage)
            .filter(Boolean);
          setMessages(normalizedMessages.length ? normalizedMessages : [
            {
              role: "ai",
              text: STARTER_MESSAGE,
            },
          ]);
        } else {
          setMessages([
            {
              role: "ai",
              text: STARTER_MESSAGE,
            },
          ]);
        }
        if (data.petContext && typeof data.petContext === "object") {
          setPetContext((prev) => ({ ...prev, ...data.petContext }));
        } else {
          setPetContext(initialPetContext);
        }
      } catch (err) {
        console.error("AI memory load failed:", err);
      }
    };

    loadMemory();
  }, [memoryDocId, initialPetContext]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const fallbackEmpatheticReply = (query, context) => {
    const q = query.toLowerCase();
    if (isFindVetNearMeQuery(q)) {
      return "I can help you find a verified vet! Please select your city below or visit our Veterinary & Health Care section.";
    }
    if (/(common dog diseases|common diseases for dogs|dog disease)/.test(q)) {
      return "Common dog diseases include skin/ear infections, GI infections, dental disease, tick-borne disease, obesity-related issues, arthritis, and kidney disease. If symptoms are persistent or severe, consult a vet promptly.";
    }
    if (/(homemade|recipe|diet plan|meal plan)/.test(q) && /(dog|puppy)/.test(q)) {
      return "A safe dog homemade template is 50% cooked lean protein, 25% cooked carbs, 15% cooked vegetables, and 10% healthy fats/fiber support, with vet-approved supplements for long-term feeding.";
    }
    if (/(homemade|recipe|diet plan|meal plan)/.test(q) && /(cat|kitten)/.test(q)) {
      return "Cats need meat-based complete diets with taurine and balanced minerals. For long-term homemade feeding, use a vet-formulated feline recipe plus supplements.";
    }
    if (q.includes("vomit") || q.includes("fever") || q.includes("pain") || q.includes("sick")) {
      return `I understand this is worrying. Based on what you shared${context.breed ? ` about your ${context.breed}` : ""}, please keep your pet hydrated and avoid self-medication. If symptoms continue or worsen, please book a vet consultation immediately.`;
    }
    if (q.includes("near") || q.includes("book") || q.includes("city")) {
      return "Sure, I will definitely help you with that. Please share your city and preferred service type, and I will suggest the best nearby options.";
    }
    return "Sure, I will definitely help you with that. Ask me anything about pet care or booking services.";
  };

  const extractPetContext = (query, prev) => {
    const lower = query.toLowerCase();
    const next = { ...prev };
    const breedMatch = query.match(/(golden retriever|labrador|german shepherd|beagle|pug|persian|indie)/i);
    const ageMatch = query.match(/(\d+)\s*(month|months|yr|year|years)/i);
    const weightMatch = query.match(/(\d+)\s*(kg|kgs)/i);

    if (breedMatch) next.breed = breedMatch[1];
    if (ageMatch) next.age = `${ageMatch[1]} ${ageMatch[2]}`;
    if (weightMatch) next.weight = `${weightMatch[1]} kg`;

    const hasHealthSignal = /(vomit|vomiting|loose motion|diarrhea|fever|not eating|loss of appetite|pain|injury|lethargy|seizure|breathing|urinary|poop|stool|sick|ill|emergency)/.test(lower);

    let detectedSymptom = "";
    if (lower.includes("vomit") || lower.includes("vomiting")) detectedSymptom = "vomiting";
    if (lower.includes("loose motion") || lower.includes("diarrhea")) detectedSymptom = "loose motion";
    if (lower.includes("fever")) detectedSymptom = "fever";
    if (lower.includes("not eating") || lower.includes("loss of appetite")) detectedSymptom = "not eating";

    if (detectedSymptom) {
      next.symptoms = detectedSymptom;
    } else if (!hasHealthSignal) {
      next.symptoms = "";
    }

    return next;
  };
  const getServiceSuggestions = (queryText) => {
    const q = String(queryText || "").trim().toLowerCase();
    if (!q) return dedupeServices(services).slice(0, 3);
    const matchedCity = normalizeText(detectCityMention(q));
    const matchedCategory = normalizeText(detectCategoryMention(q));
    const ranked = dedupeServices(
      [...services]
      .map((service) => {
        const title = normalizeText(service.title);
        const category = normalizeText(service.category);
        const city = normalizeText(service.city);
        let score = 0;
        if (matchedCity && city.includes(matchedCity)) score += 4;
        if (matchedCategory && category.includes(matchedCategory)) score += 4;
        if (q && title.includes(q)) score += 3;
        q.split(/\s+/).forEach((word) => {
          if (
            word.length > 3 &&
            !NON_SCORING_WORDS.has(word) &&
            (title.includes(word) || category.includes(word) || city.includes(word))
          ) {
            score += 1;
          }
        });
        return { ...service, _score: score };
      })
      .sort((a, b) => b._score - a._score)
    );

    const positivelyRanked = ranked.filter((service) => Number(service._score || 0) > 0);
    if (!positivelyRanked.length) {
      return [];
    }

    if (matchedCategory) {
      const categorySpecific = positivelyRanked.filter((service) =>
        normalizeText(service.category).includes(matchedCategory),
      );
      if (categorySpecific.length) {
        return categorySpecific.slice(0, 3);
      }
    }

    return positivelyRanked.slice(0, 3);
  };

  const getBookingAutomation = (queryText, candidateServices) => {
    const detectedCity = detectCityMention(queryText);
    const detectedCategory = detectCategoryMention(queryText);
    const bookingIntent = isBookingIntentQuery(queryText) || Boolean(detectedCity || detectedCategory);
    const primaryService = Array.isArray(candidateServices) && candidateServices.length ? candidateServices[0] : null;
    return {
      bookingIntent,
      city: detectedCity || primaryService?.city || "",
      category: detectedCategory || primaryService?.category || "",
      primaryService,
      needsCity: bookingIntent && !(detectedCity || primaryService?.city),
      needsCategory: bookingIntent && !(detectedCategory || primaryService?.category),
    };
  };

  const openServiceForBooking = (service, automation = null) => {
    if (!service) return;
    if (onApplyBookingIntent) {
      onApplyBookingIntent({
        city: service.city || automation?.city || "",
        category: service.category || automation?.category || "",
        search: service.title || "",
        autoScroll: false,
      });
    }
    onOpenProvider(service);
  };

  const onCityQuickReply = (cityName) => {
    const selectedCity = String(cityName || "").trim();
    if (!selectedCity) return;
    if (typeof onApplyBookingIntent === "function") {
      onApplyBookingIntent({
        city: selectedCity,
        category: "Veterinary & Health Care",
        search: "vet",
        autoScroll: true,
      });
    }
    if (typeof window !== "undefined") {
      window.location.hash = `#/services?category=veterinary&city=${encodeURIComponent(selectedCity)}`;
    }
  };

  const onPickImage = () => {
    fileInputRef.current?.click();
  };

  const onImageSelected = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) {
      return;
    }
    if (!String(file.type || "").startsWith("image/")) {
      window.alert("Please select a valid image file.");
      e.target.value = "";
      return;
    }
    if (Number(file.size || 0) > MAX_RAW_IMAGE_ATTACHMENT_BYTES) {
      window.alert("Image is too large. Please upload an image smaller than 12 MB.");
      e.target.value = "";
      return;
    }
    try {
      setPreparingImage(true);
      const optimizedFile = await optimizeImageForUpload(file, {
        maxWidth: 1400,
        maxHeight: 1400,
        quality: 0.8,
        maxOutputBytes: MAX_IMAGE_ATTACHMENT_BYTES,
        skipBelowBytes: 180 * 1024,
        outputType: "image/jpeg",
      });
      if (Number(optimizedFile?.size || 0) > MAX_IMAGE_ATTACHMENT_BYTES) {
        window.alert("Image is still too large after optimization. Please use a smaller image.");
        e.target.value = "";
        return;
      }
      const dataUrl = await readFileAsDataUrl(optimizedFile);
      setPendingImage({
        name: optimizedFile.name || file.name || "pet-image",
        type: optimizedFile.type || file.type || "image/jpeg",
        size: Number(optimizedFile.size || file.size || 0),
        dataUrl,
      });
    } catch (err) {
      console.error(err);
      window.alert("Could not read that image. Please try another file.");
    } finally {
      setPreparingImage(false);
      e.target.value = "";
    }
  };

  const onSend = async (prefillText = "") => {
    const text = String(prefillText || input).trim();
    const imageForRequest = pendingImage;
    if (loading || preparingImage) {
      return;
    }
    if (!text && !imageForRequest) {
      return;
    }

    const messageText = text || "Please analyze this pet image and suggest what to do.";
    const normalizedText = normalizeCityTyposInQuery(messageText);
    const nextContext = extractPetContext(normalizedText, petContext);
    const nextSuggestions = getServiceSuggestions(normalizedText);
    const nextAutomation = getBookingAutomation(normalizedText, nextSuggestions);
    setSuggestedServices(nextSuggestions);
    setBookingAutomation(nextAutomation);
    setPetContext(nextContext);
    setLastUserQuery(normalizedText);
    if (nextAutomation.bookingIntent && onApplyBookingIntent) {
      onApplyBookingIntent({
        city: nextAutomation.city,
        category: nextAutomation.category,
        search: normalizedText,
        autoScroll: true,
      });
      if (nextAutomation.needsCity) {
        setAutomationMessage("Share your city name so I can auto-book the nearest provider.");
      } else if (nextAutomation.needsCategory) {
        setAutomationMessage("Tell me the exact service type and I will auto-filter providers for booking.");
      } else {
        setAutomationMessage(
          `I applied filters for ${nextAutomation.city || "your city"}${nextAutomation.category ? ` · ${nextAutomation.category}` : ""}. You can open a top match below.`,
        );
      }
    } else {
      setAutomationMessage("");
    }
    const nextMessages = [...messages, {
      role: "user",
      text: messageText,
      image: imageForRequest ? {
        name: imageForRequest.name,
        type: imageForRequest.type,
        size: imageForRequest.size,
        dataUrl: imageForRequest.dataUrl,
      } : null,
    }];
    setMessages(nextMessages);
    await persistMemory(nextMessages, nextContext);
    setInput("");
    setPendingImage(null);
    setLoading(true);

    if (isFindVetNearMeQuery(normalizedText)) {
      const nearMeReply = "I can help you find a verified vet! Please select your city below or visit our Veterinary & Health Care section.";
      const next = [
        ...nextMessages,
        { role: "ai", text: nearMeReply, quickReplies: VET_CITY_CHIPS },
      ];
      setMessages(next);
      await persistMemory(next, nextContext);
      setLoading(false);
      return;
    }

    try {
      const payloadMessages = nextMessages.map((m) => ({
        role: normalizeMessageRole(m.role) === "ai" ? "assistant" : "user",
        content: normalizeMessageRole(m.role) === "user" ?
          normalizeCityTyposInQuery(m.text) :
          m.text,
      }));
      const uniqueServices = dedupeServices(services);

      const res = await fetch(AI_CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: payloadMessages,
          imageAttachment: imageForRequest ? {
            name: imageForRequest.name,
            mimeType: imageForRequest.type,
            size: imageForRequest.size,
            dataUrl: imageForRequest.dataUrl,
          } : null,
          petContext: {
            ...nextContext,
            petName: selectedPet?.name || "",
            species: selectedPet?.species || "",
          },
          services: uniqueServices.map((s) => ({
            id: s.id,
            title: s.title,
            city: s.city,
            category: s.category,
            price: s.price,
            providerId: s.providerId || "",
            providerEmail: s.providerEmail || "",
          })),
        }),
      });

      if (!res.ok) {
        let details = "AI request failed";
        try {
          const err = await res.json();
          details = err?.details || err?.error || details;
        } catch (_) {}
        throw new Error(details);
      }

      const data = await res.json();
      setAiRuntime({
        mode: String(data?.mode || ""),
        reason: String(data?.modeReason || ""),
      });
      const reply = normalizeCityTyposInQuery(
        data?.reply || fallbackEmpatheticReply(normalizedText, nextContext),
      );
      const next = [...nextMessages, { role: "ai", text: reply }];
      setMessages(next);
      await persistMemory(next, nextContext);
    } catch (err) {
      console.error(err);
      setAiRuntime({
        mode: "client_error_fallback",
        reason: String(err?.message || "client_error"),
      });
      const fallback = normalizeCityTyposInQuery(
        fallbackEmpatheticReply(normalizedText, nextContext),
      );
      const reason = err?.message ? `Reason: ${err.message}` : "Reason: unknown";
      const next = [
        ...nextMessages,
        { role: "ai", text: `${fallback}\n\n(Using backup mode. ${reason})` },
      ];
      setMessages(next);
      await persistMemory(next, nextContext);
    } finally {
      setLoading(false);
    }
  };

  const onOpenChat = () => {
    setIsOpen(true);
    setIsMinimized(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleOpen = (event) => {
      const query = event?.detail?.query;
      setIsOpen(true);
      setIsMinimized(false);
      if (typeof query === "string" && query.trim()) {
        setInput(query.trim());
      }
    };
    window.addEventListener("petaar-ai-open", handleOpen);
    return () => window.removeEventListener("petaar-ai-open", handleOpen);
  }, []);
  const clearChat = async () => {
    const starter = [{
      role: "ai",
      text: STARTER_MESSAGE,
    }];
    setMessages(starter);
    setSuggestedServices([]);
    setBookingAutomation(null);
    setAutomationMessage("");
    setLastUserQuery("");
    setPendingImage(null);
    setAiRuntime({
      mode: "",
      reason: "",
    });
    setPetContext(initialPetContext);
    if (memoryDocId) {
      try {
        await deleteDoc(doc(db, "aiMemory", memoryDocId));
      } catch (_) {}
    }
  };
  const mobileSafeBottom = "calc(env(safe-area-inset-bottom, 0px) + 8px)";
  const isMobileChatSheet = isOpen && !isMinimized && isMobileViewport;

  return (
    <div
      style={
        isMobileChatSheet
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
            }
          : {
              position: "fixed",
              right: isMobileViewport ? 8 : 16,
              bottom: isMobileViewport ? mobileSafeBottom : 16,
              zIndex: 1000,
              maxWidth: "100vw",
            }
      }
    >
      <style>{`
        @keyframes petaarPulse {
          0% { box-shadow: 0 10px 22px rgba(15, 30, 100, 0.34), 0 0 0 0 rgba(70, 120, 255, 0.26); }
          70% { box-shadow: 0 10px 22px rgba(15, 30, 100, 0.34), 0 0 0 12px rgba(70, 120, 255, 0); }
          100% { box-shadow: 0 10px 22px rgba(15, 30, 100, 0.34), 0 0 0 0 rgba(70, 120, 255, 0); }
        }
        @keyframes petaarBlink {
          0%, 44%, 48%, 100% { transform: scaleY(1); }
          46% { transform: scaleY(0.12); }
        }
        @keyframes petaarHover {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
      `}</style>

      {isMobileChatSheet && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(15, 23, 42, 0.36)",
          }}
        />
      )}

      {!isOpen && (
        <button
          onClick={onOpenChat}
          title="Open Petaar AI Assistant"
          style={{
            position: "relative",
            width: isMobileViewport ? 58 : "min(290px, 86vw)",
            minHeight: isMobileViewport ? 58 : 84,
            border: "2px solid #d7e4ff",
            background: "linear-gradient(120deg, #244db7 0%, #1b3e9f 55%, #152f7f 100%)",
            color: "#fff",
            borderRadius: isMobileViewport ? 999 : 24,
            cursor: "pointer",
            fontWeight: 800,
            boxShadow: "0 10px 22px rgba(15, 30, 100, 0.34)",
            animation: "petaarPulse 2.3s ease-in-out infinite, petaarHover 3.1s ease-in-out infinite",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isMobileViewport ? 0 : 14,
            padding: isMobileViewport ? 0 : "12px 16px 12px 14px",
            textAlign: "left",
            overflow: "hidden",
          }}
        >
          <span
            style={{
              position: "relative",
              width: isMobileViewport ? 34 : 56,
              height: isMobileViewport ? 34 : 56,
              minWidth: isMobileViewport ? 34 : 56,
              borderRadius: "50%",
              border: "2px solid rgba(220, 234, 255, 0.95)",
              background: "radial-gradient(circle at 36% 32%, #5e92ff 0%, #2c58cb 62%, #1d3f9d 100%)",
              boxShadow: "inset 0 -8px 12px rgba(10, 26, 83, 0.36)",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: isMobileViewport ? -7 : -14,
                left: isMobileViewport ? 6 : 5,
                width: isMobileViewport ? 8 : 17,
                height: isMobileViewport ? 12 : 24,
                borderRadius: "75% 25% 65% 20%",
                transform: "rotate(-28deg)",
                border: "2px solid rgba(220, 234, 255, 0.95)",
                background: "linear-gradient(180deg, #5e92ff 0%, #3363d9 100%)",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: isMobileViewport ? -7 : -14,
                right: isMobileViewport ? 6 : 5,
                width: isMobileViewport ? 8 : 17,
                height: isMobileViewport ? 12 : 24,
                borderRadius: "25% 75% 20% 65%",
                transform: "rotate(28deg)",
                border: "2px solid rgba(220, 234, 255, 0.95)",
                background: "linear-gradient(180deg, #5e92ff 0%, #3363d9 100%)",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: isMobileViewport ? 12 : 19,
                left: isMobileViewport ? 9 : 16,
                width: isMobileViewport ? 3 : 7,
                height: isMobileViewport ? 4 : 9,
                borderRadius: "50%",
                background: "#f8fbff",
                animation: "petaarBlink 3.2s ease-in-out infinite",
                transformOrigin: "center",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: isMobileViewport ? 12 : 19,
                right: isMobileViewport ? 9 : 16,
                width: isMobileViewport ? 3 : 7,
                height: isMobileViewport ? 4 : 9,
                borderRadius: "50%",
                background: "#f8fbff",
                animation: "petaarBlink 3.2s ease-in-out infinite",
                transformOrigin: "center",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: isMobileViewport ? 19 : 31,
                left: isMobileViewport ? 14 : 25,
                width: isMobileViewport ? 4 : 7,
                height: isMobileViewport ? 3 : 5,
                borderRadius: "55% 55% 70% 70%",
                background: "#ffd6df",
              }}
            />
          </span>

          {!isMobileViewport && (
            <span style={{ lineHeight: 1.1 }}>
              <span style={{ display: "block", fontSize: 24, letterSpacing: 0.1 }}>Ask Petaar AI</span>
            </span>
          )}
        </button>
      )}

      {isOpen && isMinimized && (
        <div
          style={{
            width: "min(320px, 92vw)",
            background: "#fff",
            border: "1px solid #dbe3ee",
            borderRadius: 10,
            boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
            padding: "8px 10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong>Petaar AI Assistant</strong>
            <span
              title={getAiRuntimeHint(aiRuntime.mode, aiRuntime.reason)}
              style={{
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 999,
                padding: "2px 8px",
                background: String(aiRuntime.mode || "").toLowerCase() === "openai" ?
                  "rgba(16, 185, 129, 0.15)" :
                  "rgba(251, 191, 36, 0.2)",
                border: String(aiRuntime.mode || "").toLowerCase() === "openai" ?
                  "1px solid rgba(110, 231, 183, 0.7)" :
                  "1px solid rgba(252, 211, 77, 0.8)",
                color: String(aiRuntime.mode || "").toLowerCase() === "openai" ? "#065f46" : "#92400e",
              }}
            >
              {getAiRuntimeLabel(aiRuntime.mode)}
            </span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setIsMinimized(false)}>▢</button>
            <button onClick={() => { setIsMinimized(false); setIsOpen(false); }}>✕</button>
          </div>
        </div>
      )}

      {isOpen && !isMinimized && (
        <div
          style={{
            position: isMobileViewport ? "relative" : "static",
            width: isMobileViewport ? "100vw" : 364,
            maxWidth: isMobileViewport ? "100vw" : "94vw",
            height: isMobileViewport ? "min(86dvh, 760px)" : "auto",
            maxHeight: isMobileViewport ? "86dvh" : "none",
            background: "#fff",
            border: "1px solid #dbe6f5",
            borderRadius: isMobileViewport ? "18px 18px 0 0" : 24,
            boxShadow: "0 24px 44px rgba(15, 23, 42, 0.24)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            paddingBottom: isMobileViewport ? "env(safe-area-inset-bottom, 0px)" : 0,
          }}
        >
          <div
            style={{
              position: "relative",
              background: "linear-gradient(112deg, #3157e5 0%, #45b8ff 100%)",
              color: "#fff",
              padding: isMobileViewport ? "10px 10px 18px" : "14px 14px 22px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleLogoHomeClick}
                  aria-label="Go to home page"
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <img
                    src="/petaar-logo.jpeg"
                    alt="Petaar AI"
                    style={{
                      width: isMobileViewport ? 96 : 112,
                      height: isMobileViewport ? 32 : 36,
                      borderRadius: 999,
                      border: "2px solid rgba(255,255,255,0.86)",
                      objectFit: "contain",
                      background: "#fff",
                      padding: "3px 8px",
                      boxSizing: "border-box",
                    }}
                  />
                </button>
                <div style={{ fontWeight: 800, fontSize: isMobileViewport ? 19 : 24, lineHeight: 1.2 }}>Chat with Petaar AI</div>
                <small
                  title={getAiRuntimeHint(aiRuntime.mode, aiRuntime.reason)}
                  style={{ opacity: 0.95 }}
                >
                  {getAiRuntimeLabel(aiRuntime.mode)}
                </small>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={clearChat}
                  title="Clear chat"
                  style={{
                    height: isMobileViewport ? 26 : 28,
                    padding: isMobileViewport ? "0 8px" : "0 10px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.6)",
                    background: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Clear
                </button>
                <button
                  onClick={clearChat}
                  title="Reset chat"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.55)",
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  ↻
                </button>
                  <button
                    onClick={() => setIsMinimized(true)}
                    title="Minimize"
                  style={{
                    width: isMobileViewport ? 30 : 28,
                    height: isMobileViewport ? 30 : 28,
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.55)",
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  −
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  title="Close"
                  style={{
                    width: isMobileViewport ? 30 : 28,
                    height: isMobileViewport ? 30 : 28,
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.55)",
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#22c55e",
                  boxShadow: "0 0 0 4px rgba(34, 197, 94, 0.18)",
                }}
              />
              <span>We&apos;re online!</span>
            </div>
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -14,
                height: 28,
                background: "#fff",
                borderTopLeftRadius: "55% 100%",
                borderTopRightRadius: "55% 100%",
              }}
            />
          </div>

          <div
            style={{
              padding: isMobileViewport ? "12px 10px calc(12px + env(safe-area-inset-bottom, 0px))" : "18px 12px 12px",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                flex: "0 0 auto",
                minHeight: isMobileViewport ? 120 : 140,
                maxHeight: isMobileViewport ? 220 : 300,
                overflowY: "auto",
                marginBottom: 10,
                padding: "2px 4px 8px",
              }}
            >
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  style={{
                    margin: "10px 0",
                    display: "flex",
                    justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      maxWidth: "88%",
                      padding: "10px 12px",
                      borderRadius: m.role === "user" ? "16px 16px 6px 16px" : "16px 16px 16px 6px",
                      background: m.role === "user" ? "linear-gradient(180deg, #4d87ff 0%, #3a72f2 100%)" : "#eef2f7",
                      color: m.role === "user" ? "#fff" : "#1f2937",
                      border: m.role === "user" ? "1px solid #2f64dc" : "1px solid #dde5f1",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                    }}
                  >
                    {m.text}
                    {m.image?.dataUrl && (
                      <div style={{ marginTop: 8 }}>
                        <img
                          src={m.image.dataUrl}
                          alt={m.image.name || "Attached pet image"}
                          style={{
                            width: 170,
                            maxWidth: "100%",
                            borderRadius: 10,
                            border: "1px solid rgba(148, 163, 184, 0.6)",
                            display: "block",
                          }}
                        />
                      </div>
                    )}
                    {m.image && !m.image.dataUrl && (
                      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
                        Attached image: {m.image.name || "pet image"}
                      </div>
                    )}
                    {m.role === "ai" && Array.isArray(m.quickReplies) && m.quickReplies.length > 0 && (
                      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {m.quickReplies.map((chip) => (
                          <button
                            key={`${idx}-${chip}`}
                            onClick={() => onCityQuickReply(chip)}
                            style={{
                              borderRadius: 999,
                              border: "1px solid #8eb3ff",
                              background: "#fff",
                              color: "#2f64dc",
                              padding: "4px 10px",
                              fontWeight: 700,
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ margin: "10px 0", display: "flex", justifyContent: "flex-start" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "10px 12px",
                      borderRadius: "16px 16px 16px 6px",
                      background: "#eef2f7",
                      border: "1px solid #dde5f1",
                    }}
                  >
                    Petaar AI is typing...
                  </span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {pendingImage && (
              <div
                style={{
                  marginBottom: 10,
                  border: "1px solid #dbe3ee",
                  borderRadius: 10,
                  background: "#f8fbff",
                  padding: 8,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <img
                  src={pendingImage.dataUrl}
                  alt={pendingImage.name || "Pending upload"}
                  style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 8, border: "1px solid #dbe3ee" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {pendingImage.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>
                    {(Number(pendingImage.size || 0) / 1024).toFixed(0)} KB
                  </div>
                </div>
                <button
                  onClick={() => setPendingImage(null)}
                  style={{
                    borderRadius: 999,
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    cursor: "pointer",
                    padding: "5px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Remove
                </button>
              </div>
            )}
            {preparingImage && (
              <div
                style={{
                  marginBottom: 10,
                  border: "1px solid #dbe3ee",
                  borderRadius: 10,
                  background: "#f8fbff",
                  padding: 8,
                  fontSize: 12,
                  color: "#1e3a8a",
                  fontWeight: 700,
                }}
              >
                Optimizing image for faster upload...
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {quickPromptChips.map((chip) => (
                <button
                  key={chip}
                  onClick={() => onSend(chip)}
                  disabled={loading}
                  style={{
                    borderRadius: 999,
                    border: "1px solid #8eb3ff",
                    background: "#fff",
                    color: "#2f64dc",
                    padding: "5px 12px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  {chip}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid #d8e3f4",
                borderRadius: 999,
                padding: "6px 6px 6px 10px",
                background: "#fff",
                boxShadow: "0 10px 20px rgba(15, 23, 42, 0.08)",
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onImageSelected}
              />
              <button
                onClick={onPickImage}
                disabled={loading || preparingImage}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  cursor: "pointer",
                  fontSize: 15,
                }}
                title="Attach a pet photo"
              >
                📎
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onSend();
                    return;
                  }
                  if (e.key === "Tab" && input.trim()) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                placeholder="Enter your message..."
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 14,
                  color: "#0f172a",
                }}
              />
              <button
                onClick={() => onSend()}
                disabled={loading || preparingImage}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  background: "linear-gradient(135deg, #3578ff 0%, #2ea9ff 100%)",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 16,
                  fontWeight: 700,
                  boxShadow: "0 8px 14px rgba(47, 100, 220, 0.35)",
                }}
              >
                ➤
              </button>
            </div>

            {bookingAutomation?.bookingIntent && (
              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #dbe3ee",
                  background: "#f8fbff",
                  borderRadius: 10,
                  padding: 8,
                  display: "grid",
                  gap: 6,
                }}
              >
                <strong style={{ fontSize: 13 }}>AI Booking Automation</strong>
                {automationMessage && (
                  <small style={{ color: "#475569" }}>{automationMessage}</small>
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => onApplyBookingIntent?.({
                      city: bookingAutomation.city,
                      category: bookingAutomation.category,
                      search: lastUserQuery,
                      autoScroll: true,
                    })}
                    disabled={!onApplyBookingIntent}
                    style={{ borderRadius: 999, border: "1px solid #cbd5e1", background: "#fff", padding: "5px 10px", cursor: "pointer" }}
                  >
                    Apply Booking Filters
                  </button>
                  {bookingAutomation.primaryService && (
                    <button
                      onClick={() => openServiceForBooking(bookingAutomation.primaryService, bookingAutomation)}
                      style={{ borderRadius: 999, border: "1px solid #cbd5e1", background: "#fff", padding: "5px 10px", cursor: "pointer" }}
                    >
                      Open Top Match
                    </button>
                  )}
                </div>
              </div>
            )}

            {suggestedServices.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {!user && (
                  <div style={{ marginBottom: 6 }}>
                    <small style={{ color: "#64748b" }}>Login to save AI chat and pet memory.</small>
                  </div>
                )}
                <div style={{ display: "grid", gap: 8 }}>
                  <strong style={{ fontSize: 13 }}>Suggested Providers</strong>
                  {suggestedServices.map((service) => (
                    <div
                      key={`ai-suggest-${service.id}`}
                      style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 8, background: "#fff" }}
                    >
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        {service.title} - {service.city} - ₹{service.price}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                        <button
                          onClick={() => onApplyBookingIntent?.({
                            city: service.city,
                            category: service.category,
                            search: service.title || "",
                            autoScroll: true,
                          })}
                          disabled={!onApplyBookingIntent}
                          style={{ borderRadius: 999, border: "1px solid #cbd5e1", background: "#fff", padding: "5px 10px", cursor: "pointer" }}
                        >
                          Filter
                        </button>
                        <button
                          onClick={() => openServiceForBooking(service, bookingAutomation)}
                          style={{ borderRadius: 999, border: "1px solid #cbd5e1", background: "#fff", padding: "5px 10px", cursor: "pointer" }}
                        >
                          Open & Book
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
